import { StatusBadge, type StatusTone } from "@/components/common/StatusBadge";
import { unitLabelFor } from "@/components/common/UnitPicker";
import { expiryLabel, stockStatus, type StockStatus } from "@/lib/expiry";
import { formatMoney, formatQuantity } from "@/lib/format";
import { cn } from "@/lib/utils";

export type StockRow = {
  id: string;
  name: string;
  measurementUnit: string;
  quantity: number;
  expiryDate?: string | null;
  /** Products carry a unit price; raw materials do not. */
  unitPrice?: number;
};

const STATUS: Record<StockStatus, { tone: StatusTone; label: string }> = {
  ok: { tone: "ok", label: "En stock" },
  expiring: { tone: "warning", label: "Bientôt périmé" },
  expired: { tone: "danger", label: "Périmé" },
  out: { tone: "danger", label: "Épuisé" },
};

const STATUS_SURFACE: Record<StockStatus, string> = {
  ok: "bg-success-soft/20",
  expiring: "bg-warning-soft/35",
  expired: "bg-destructive-soft/30",
  out: "bg-destructive-soft/30",
};

/**
 * Shared by both stock screens: a table on a wide screen, cards on a phone.
 * The value column only appears when the rows carry a price.
 */
export function StockItemList({
  rows,
  currency,
  quantityLabel = "Reste en stock",
}: {
  rows: StockRow[];
  currency: string;
  quantityLabel?: string;
}) {
  const hasValue = rows.some((row) => row.unitPrice !== undefined);

  return (
    <>
      {/* Scrolls inside its own box: next to the production panel the columns
          no longer fit, and clipping the status badge hid the row's meaning. */}
      <div className="hidden overflow-x-auto rounded-xl border border-border bg-card md:block">
        <table className="w-full min-w-[640px] text-left">
          <thead className="bg-muted/60">
            <tr className="text-[13px] font-semibold text-muted-foreground">
              <th className="px-4 py-3">Nom</th>
              <th className="w-32 px-4 py-3">{quantityLabel}</th>
              {hasValue ? <th className="w-36 px-4 py-3 text-right">Valeur</th> : null}
              <th className="w-32 px-4 py-3">Péremption</th>
              <th className="w-36 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const status = stockStatus(row.quantity, row.expiryDate);
              const { tone, label } = STATUS[status];
              return (
                <tr key={row.id} className="border-t border-muted">
                  <td className={cn("px-4 py-3.5 font-medium", STATUS_SURFACE[status])}>{row.name}</td>
                  <td className="px-4 py-3.5 tabular-nums">
                    <span className={status === "out" ? "font-semibold text-destructive" : undefined}>
                      {formatQuantity(row.quantity, unitLabelFor(row.measurementUnit, row.quantity))}
                    </span>
                  </td>
                  {hasValue ? (
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      {row.unitPrice === undefined ? "—" : formatMoney(row.unitPrice * row.quantity, currency)}
                    </td>
                  ) : null}
                  <td className="px-4 py-3.5 text-[15px] text-muted-foreground">{expiryLabel(row.expiryDate)}</td>
                  <td className="px-4 py-3.5">
                    <StatusBadge tone={tone} className="whitespace-nowrap">
                      {label}
                    </StatusBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => {
          const status = stockStatus(row.quantity, row.expiryDate);
          const { tone, label } = STATUS[status];
          return (
            <li key={row.id} className="rounded-xl border border-border bg-card p-4">
              <div
                className={cn(
                  "-mx-2 -mt-2 flex items-start justify-between gap-3 rounded-lg px-2 py-2",
                  STATUS_SURFACE[status],
                )}
              >
                <p className="font-semibold">{row.name}</p>
                <StatusBadge tone={tone}>{label}</StatusBadge>
              </div>
              <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                <span className="text-muted-foreground">
                  {quantityLabel} :{" "}
                  <span className="font-medium tabular-nums text-foreground">
                    {formatQuantity(row.quantity, unitLabelFor(row.measurementUnit, row.quantity))}
                  </span>
                </span>
                {row.unitPrice !== undefined ? (
                  <span className="text-muted-foreground">
                    Valeur :{" "}
                    <span className="font-medium tabular-nums text-foreground">
                      {formatMoney(row.unitPrice * row.quantity, currency)}
                    </span>
                  </span>
                ) : null}
                {row.expiryDate ? (
                  <span className="text-muted-foreground">
                    Péremption : <span className="font-medium text-foreground">{expiryLabel(row.expiryDate)}</span>
                  </span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
